export const normalizeScanValue = (value = '') => {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
};

const SCAN_VALUE_PREFIXES = ['QR', 'GCENTRA', 'PRODUCT', 'ROUTE', 'DESTINATION', 'LOAD'];

const normalizeComparableValue = (value = '') => {
  let normalized = normalizeScanValue(value);
  let previousValue = '';

  while (normalized && previousValue !== normalized) {
    previousValue = normalized;
    for (const prefix of SCAN_VALUE_PREFIXES) {
      normalized = normalized.replace(new RegExp(`^${prefix}:`, 'i'), '');
    }
  }

  return normalized.replace(/[^A-Z0-9]+/g, '');
};

const uniqueTokens = (values = []) => {
  return Array.from(
    new Set(
      values
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  );
};

const buildCandidate = ({ targetType, label, entity, tokens, meta = {} }) => ({
  targetType,
  label,
  entity,
  meta,
  tokens: uniqueTokens(tokens)
});

export const buildScanCandidates = ({
  destination = null,
  route = null,
  products = [],
  operationalLoads = [],
  incomingOperationalLoads = [],
} = {}) => {
  const candidates = [];

  if (route) {
    candidates.push(
      buildCandidate({
        targetType: 'route',
        label: route.name || route.title || `Ruta ${route.id || ''}`.trim(),
        entity: route,
        tokens: [route.id, route.routeCode, route.referenceCode, route.name, route.title]
      })
    );
  }

  if (destination) {
    candidates.push(
      buildCandidate({
        targetType: 'destination',
        label: destination.name || destination.address || `Destino ${destination.id || ''}`.trim(),
        entity: destination,
        tokens: [
          destination.id,
          destination.referenceCode,
          destination.name,
          destination.address,
          destination.contactName,
          destination.contactPhone
        ]
      })
    );
  }

  operationalLoads.forEach((load) => {
    candidates.push(
      buildCandidate({
        targetType: 'operational_load',
        label: load.title || load.stockProductNameSnapshot || load.referenceCode || 'Carga operativa',
        entity: load,
        meta: { direction: 'outgoing' },
        tokens: [
          load.id,
          load.referenceCode,
          load.title,
          load.stockProductId,
          load.stockProductNameSnapshot,
          load.stockSkuSnapshot,
          load.stockBarcodeSnapshot,
          load.stockQrCodeSnapshot,
          load.barcodeSnapshot,
          load.qrCodeSnapshot,
          load.stockOutboundOrderId,
          load.stockOutboundOrderNumber,
          load.stockOrderReference,
          load.purchaseLineId,
          load.purchaseNumber
        ]
      })
    );
  });

  incomingOperationalLoads.forEach((load) => {
    candidates.push(
      buildCandidate({
        targetType: 'incoming_load',
        label: load.title || load.stockProductNameSnapshot || load.referenceCode || 'Carga recibida',
        entity: load,
        meta: { direction: 'incoming' },
        tokens: [
          load.id,
          load.loadId,
          load.referenceCode,
          load.title,
          load.stockProductId,
          load.stockProductNameSnapshot,
          load.stockSkuSnapshot,
          load.stockBarcodeSnapshot,
          load.stockQrCodeSnapshot,
          load.barcodeSnapshot,
          load.qrCodeSnapshot,
          load.stockOutboundOrderId,
          load.stockOutboundOrderNumber,
          load.stockOrderReference,
          load.purchaseLineId,
          load.purchaseNumber
        ]
      })
    );
  });

  products.forEach((product) => {
    candidates.push(
      buildCandidate({
        targetType: 'product',
        label: product.name || product.sku || product.barcode || 'Producto',
        entity: product,
        tokens: [
          product.id,
          product.name,
          product.sku,
          product.barcode,
          product.qrCode,
          product.category,
          product.brand,
          product.featureLabel,
          product.model
        ]
      })
    );
  });

  return candidates;
};

export const resolveScanValue = (value, context = {}) => {
  const scannedValue = normalizeScanValue(value);
  const compactValue = normalizeComparableValue(value);

  if (!compactValue) {
    return {
      status: 'empty',
      scannedValue,
      targetType: 'unknown',
      match: null,
      matches: []
    };
  }

  const candidates = buildScanCandidates(context);
  const matches = candidates.filter((candidate) =>
    candidate.tokens.some((token) => {
      const compactToken = normalizeComparableValue(token);
      if (!compactToken) return false;
      return compactToken === compactValue || compactToken.includes(compactValue) || compactValue.includes(compactToken);
    })
  );

  if (matches.length === 0) {
    return {
      status: 'not_found',
      scannedValue,
      targetType: 'unknown',
      match: null,
      matches: []
    };
  }

  if (matches.length > 1) {
    const loadMatches = matches.filter((match) =>
      ['operational_load', 'incoming_load'].includes(match.targetType)
    );
    if (loadMatches.length === 1) {
      const selectedLoad = loadMatches[0];
      const loadProductId = String(
        selectedLoad.entity?.stockProductId ||
        selectedLoad.entity?.productId ||
        ''
      ).trim();
      const allExtraMatchesBelongToLoad = matches
        .filter((match) => match !== selectedLoad)
        .every((match) => {
          if (match.targetType !== 'product') return false;
          return loadProductId && String(match.entity?.id || '').trim() === loadProductId;
        });

      if (allExtraMatchesBelongToLoad) {
        return {
          status: 'found',
          scannedValue,
          targetType: selectedLoad.targetType,
          match: selectedLoad,
          matches
        };
      }
    }

    return {
      status: 'multiple',
      scannedValue,
      targetType: 'multiple',
      match: null,
      matches
    };
  }

  return {
    status: 'found',
    scannedValue,
    targetType: matches[0].targetType,
    match: matches[0],
    matches
  };
};
